import { useEffect, useState } from 'react';
import { TendersListActions } from './actions';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { Tender, TenderFilter } from '../../../../shared/types/tender';
import { ISelect } from '../../../../shared/types/shared';
import { TableTooltip } from '../../../components/general/table';
import { useNavigate } from 'react-router-dom';
import { asyncGetTenders } from '../../../../services/tender.service';
import { RootState } from '../../../../redux/store';
import { useSelector } from 'react-redux';
import { isAdminUser } from '../../../../shared/utils/user';
import { DateTooltip } from '../../../components/general/dateTooltip';

export const useTendersList = (filters?: TenderFilter) => {
    const navigate = useNavigate();
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};
    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Tender[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<TenderFilter>({});

    const reloadData = () => {
        setLoading(true);
        asyncGetTenders(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    };

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<Tender>[] = [
        {
            field: 'actions',
            headerName: 'Acciones',
            width: isAdminUser(type) ? 150 : 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => (
                <TendersListActions id={row.id} reloadData={reloadData} />
            ),
        },
        {
            field: 'bidder',
            headerName: 'Nombre proponente',
            minWidth: 150,
            flex: 0.6,
            renderCell: (row) => <TableTooltip value={row.row.bidder || ''} />,
        },
        {
            field: 'nit',
            headerName: 'NIT',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => <TableTooltip value={row.row.nit || ''} />,
        },
        {
            field: 'createdAt',
            headerName: 'Fecha de creación',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => (
                <DateTooltip date={row.row.createdAt} format="DD/MM/YYYY" />
            ),
        },
        {
            field: 'status',
            headerName: 'Etapa',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => <TableTooltip value={row.row.status || ''} />,
        },
        {
            field: 'statusProcess',
            headerName: 'Etapa de proceso',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => {
                const statusProcess = row.row.statusProcess;
                const value =
                    typeof statusProcess === 'string'
                        ? statusProcess
                        : (statusProcess as ISelect)?.name ?? '';
                return <TableTooltip value={value} />;
            },
        },
        {
            field: 'createdBy',
            headerName: 'Creado por',
            minWidth: 200,
            flex: 0.8,
            renderCell: (row) => (
                <TableTooltip
                    value={
                        row.row.createdBy
                            ? `(${row.row.createdBy.username}) ${row.row.createdBy.name} ${row.row.createdBy.surname}`
                            : ''
                    }
                />
            ),
        },
    ];

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetTenders(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        navigate,
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
